import React, { forwardRef, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  Slide,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CopilotIcon from "components/shared/CopilotIcon/CopilotIcon";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetCopilotUserDetailQuery } from "state/rtkQuery/copilot/copilotQueries";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import {
  buildModelTrendData,
  getTickInterval,
  MODEL_LINE_COLORS,
  ModelTrendTooltip,
} from "./chartUtils";
import { useCopilotBudgetCapabilities } from "components/RequirePermission/useCapabilities";
import { formatLocalDateTime } from "utils/formatLocalDateTime";
import { getBudgetTextColor } from "./budgetColors";
import { DEFAULT_DELOITTE_OVERAGE_RATE } from "./useDashboardSettings";
import UserRequestsTab from "./UserRequestsTab";
import {
  getJoinedOrganizations,
  getLicensedOrgIds,
  UserCostBillingCard,
  UserOrganizationsTable,
} from "pages/admin/components/UserDetailsModal/UserProfileSections";

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const ACCENT = "#26890d";
const DISCOUNT_GREEN = "#26890d";
const DELOITTE_PURPLE = "#7B5EA7";
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";
const USAGE_YELLOW = "#FFCD00";

// Deloitte's negotiated overage rate vs the $0.01/AIC list price — see ModelTable.jsx.
const LIST_OVERAGE_RATE = 0.01;

const AVATAR_COLORS = ["#1F7AA8", "#43B02A", "#E2231A", "#F28C00", "#7B5EA7", "#0097D7", "#E87722"];

const getBudgetColor = (pct) => {
  if (pct >= 80) return USAGE_RED;
  if (pct >= 50) return USAGE_ORANGE;
  return USAGE_YELLOW;
};

const getInitials = (fullName) => {
  if (!fullName) return "?";
  const parts = fullName.replace(",", "").trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : parts[0][0].toUpperCase();
};

const getAvatarColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const fmt = (v) => (v ?? 0).toLocaleString("en-AU", { maximumFractionDigits: 0 });
const dashIfMissing = (v, format) => (v == null ? "—" : format(v));
const fmtUSD = (v) =>
  "$" + (v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const hCell = {
  fontWeight: 700,
  fontSize: "0.7rem",
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#f7f7f7",
  borderBottom: "2px solid #e8e8e8",
  py: 1.2,
  whiteSpace: "nowrap",
};

const bCell = {
  fontSize: "0.82rem",
  color: "#1a1a1a",
  py: 1,
  borderBottom: "1px solid #f0f0f0",
};

const MiniStat = ({ label, value, color }) => (
  <Box
    sx={{
      flex: 1,
      backgroundColor: "rgba(255,255,255,0.07)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "10px",
      px: 2,
      py: 1.2,
    }}
  >
    <Typography
      sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.06em" }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "1.05rem", fontWeight: 700, color: color ?? "#fff", lineHeight: 1.3, mt: 0.2 }}
    >
      {value}
    </Typography>
  </Box>
);

const BudgetMetaDot = () => (
  <Typography component="span" aria-hidden="true" sx={{ color: "#ccc", fontSize: "0.9rem", lineHeight: 1 }}>
    ·
  </Typography>
);

// `profile` (optional) merges the user's profile into this modal — { user, orgIdToName,
// costCenterById, isLoading, error }, where user is the GET /users record. Without it the
// modal is the plain Copilot usage view, as before.
const UserDetailModal = ({ user, open, onClose, orgId, costCenterId, platformId, fromDate, toDate, deloitteOverageRate, footerNote, profile, canViewUsage = true }) => {
  const effectiveDeloitteRate = deloitteOverageRate ?? DEFAULT_DELOITTE_OVERAGE_RATE;
  const [activeTab, setActiveTab] = useState(profile ? "overview" : "summary");
  const [expandedDays, setExpandedDays] = useState({});
  // Same permission as the Budget Requests list, which is what feeds the "Budget Requests" tab.
  const { canReadBudgetRequests } = useCopilotBudgetCapabilities(orgId);
  const showRequestsTab = Boolean(orgId && platformId && canReadBudgetRequests);
  const profileUser = profile?.user ?? null;
  const joinedOrgs = useMemo(() => getJoinedOrganizations(profileUser, platformId), [profileUser, platformId]);
  const licensedOrgIds = useMemo(
    () => new Set([...getLicensedOrgIds(profileUser), ...(user?.licensed_organizations ?? []), ...(user?.budget?.licensed_organization_ids ?? [])]),
    [profileUser, user]
  );

  // Fetch day-by-day detail from API 2 — skipped when no date range is given
  // (e.g. opened from the all-time Budget-at-risk list), falling back to the
  // basic row data already available via resolvedUser below.
  // Budget-request rows carry licensed_organizations. Usage is looked up per
  // org, so if the user is also licensed in a different org the numbers for
  // this one are not representative — we say so instead of showing them.
  const otherLicensedOrgIds = (user?.licensed_organizations ?? []).filter((id) => id !== orgId);
  const licensedElsewhere = otherLicensedOrgIds.length > 0;

  const { data: detailData, isFetching: detailLoading, error: detailError } = useGetCopilotUserDetailQuery(
    { email: user?.email, orgId, costCenterId, platformId, fromDate, toDate },
    {
      skip: !user || licensedElsewhere || !(orgId || costCenterId) || !platformId || !fromDate || !toDate,
    }
  );

  // Resolves budget.licensed_organization_ids to org names below. Called
  // with no args so it shares the RTK Query cache with any other caller on
  // the page making the same identical call (e.g. JoinedTab's own org list).
  const { data: allOrgs } = useGetActiveOrganizationsQuery(undefined, { skip: !open });
  const orgIdToName = useMemo(
    () => Object.fromEntries((allOrgs ?? []).map((org) => [org._id, org.name])),
    [allOrgs]
  );

  // Merge: use API 2 detail once it's resolved for the current args, fall
  // back to basic info from API 1 row while the detail fetch is in flight —
  // RTK Query otherwise keeps the previous user's `data` around during a
  // refetch, which would flash their stale detail for the newly-selected user.
  const detailIsForCurrentUser = detailData?.email === user?.email;
  const resolvedUser = !detailLoading && detailIsForCurrentUser ? detailData : user;
  const hasDailyUsage = Boolean(resolvedUser?.daily_usage?.length);
  // The usage API answers 404 ("No copilot usage found for user ... in the
  // given date range") when there's nothing for the period — show that
  // instead of a row of zeros. Ignore a stale error from a previous user.
  const noUsageFound = !detailLoading && detailError?.status === 404 && !detailIsForCurrentUser;

  // API returns daily_usage pre-sorted ascending by date.
  const dailyUsage = resolvedUser?.daily_usage ?? [];

  // API pre-aggregates the per-model summary (usage_stats_summary) and totals;
  // fall back to client-side aggregation only for responses that predate that shape.
  const usageItems = useMemo(() => {
    if (resolvedUser?.usage_stats_summary) return resolvedUser.usage_stats_summary;

    if (hasDailyUsage) {
      const modelMap = {};
      dailyUsage.forEach((day) => {
        const items = day.usage_stats ?? day.usageItems ?? [];
        items.forEach((item) => {
          if (!modelMap[item.model]) {
            modelMap[item.model] = {
              ...item,
              grossQuantity: 0, grossAmount: 0,
              discountQuantity: 0, discountAmount: 0,
              netQuantity: 0, netAmount: 0,
            };
          }
          const m = modelMap[item.model];
          m.grossQuantity += item.grossQuantity ?? 0;
          m.grossAmount += item.grossAmount ?? 0;
          m.discountQuantity += item.discountQuantity ?? 0;
          m.discountAmount += item.discountAmount ?? 0;
          m.netQuantity += item.netQuantity ?? 0;
          m.netAmount += item.netAmount ?? 0;
        });
      });
      return Object.values(modelMap).sort((a, b) => b.grossQuantity - a.grossQuantity);
    }
    const items = resolvedUser?.usageItems ?? [];
    return [...items].sort((a, b) => b.grossQuantity - a.grossQuantity);
  }, [resolvedUser, hasDailyUsage, dailyUsage]);

  // Pivot daily usage into chart rows. A single user touches few models, so chart
  // them all (topCount Infinity) instead of the dashboard's top-5 + "All other".
  const { rows: trendRows, modelKeys: trendModelKeys } = useMemo(
    () =>
      buildModelTrendData(
        dailyUsage.map((day) => ({
          usage_date: day.date,
          usage_stats: day.usage_stats ?? day.usageItems ?? [],
        })),
        Infinity
      ),
    [dailyUsage]
  );

  useEffect(() => {
    if (user) {
      setActiveTab(profile ? "overview" : "summary");
      setExpandedDays(
        user.daily_usage?.length === 1 ? { [user.daily_usage[0].date]: true } : {}
      );
    }
  }, [user?.email]);

  if (!user) return null;

  const initials = getInitials(resolvedUser?.full_name ?? resolvedUser?.user_name ?? user.email);
  const avatarColor = getAvatarColor(user.email ?? "");
  const { budget } = resolvedUser ?? user;
  // The per-user usage response carries the cost center's name (detail view only).
  const costCenterName = resolvedUser?.cost_center_name ?? user?.cost_center_name ?? null;
  const hasBar = Boolean(budget);
  const budgetPct =
    hasBar && budget.budget_amount > 0
      ? Math.min((budget.consumed_amount / budget.budget_amount) * 100, 100)
      : 0;
  const isOverBudget = hasBar && budget.consumed_amount > budget.budget_amount;
  const barColor = getBudgetColor(budgetPct);
  const otherLicensedOrgNames = otherLicensedOrgIds.map((id) => orgIdToName[id] ?? id);
  // Budget-request rows expose the budget as current_budget (amount + scope)
  // rather than the dashboard's budget object with consumed amounts.
  const requestBudget = !hasBar ? user.current_budget : null;
  const licensedOrgNames = (budget?.licensed_organization_ids ?? []).map(
    (id) => orgIdToName[id] ?? id
  );

  // API returns pre-computed totals; fall back to summing usageItems for older response shapes.
  const totals = resolvedUser?.totals;
  const totalGross = totals?.grossQuantity ?? usageItems.reduce((s, i) => s + (i.grossQuantity ?? 0), 0);
  const totalGrossAmt = totals?.grossAmount ?? usageItems.reduce((s, i) => s + (i.grossAmount ?? 0), 0);
  const totalDiscount = totals?.discountQuantity ?? usageItems.reduce((s, i) => s + (i.discountQuantity ?? 0), 0);
  const totalDiscountAmt = totals?.discountAmount ?? usageItems.reduce((s, i) => s + (i.discountAmount ?? 0), 0);
  const totalNet = totals?.netQuantity ?? usageItems.reduce((s, i) => s + (i.netQuantity ?? 0), 0);
  const totalNetAmt = totals?.netAmount ?? usageItems.reduce((s, i) => s + (i.netAmount ?? 0), 0);
  const totalOverageDiscountAmt = usageItems.reduce(
    (s, i) => s + (i.overageDiscountAmount ?? (i.netQuantity ?? 0) * (LIST_OVERAGE_RATE - effectiveDeloitteRate)),
    0
  );
  const totalNetOverageCostAmt = usageItems.reduce(
    (s, i) => s + (i.netOverageCostAmount ?? (i.netQuantity ?? 0) * effectiveDeloitteRate),
    0
  );

  const toggleDay = (dateStr) =>
    setExpandedDays((prev) => ({ ...prev, [dateStr]: !prev[dateStr] }));

  const fmtDate = (dateStr) => {
    const [y, m, d] = dateStr.split("-");
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${Number.parseInt(d, 10)} ${MONTHS[Number.parseInt(m, 10) - 1]} ${y}`;
  };

  const usageBreakdown = usageItems.length > 0 && (
    <>

        <Box sx={{ px: 3, pt: 2.5, pb: 0.5 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "#555",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Model Usage Breakdown{hasDailyUsage ? " - Aggregated" : ""}
          </Typography>
        </Box>

        <TableContainer sx={{ px: 3, pb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...hCell, pl: 0 }}>Model</TableCell>
                <TableCell sx={hCell} align="right">Gross AIC</TableCell>
                <TableCell sx={hCell} align="right">Gross $</TableCell>
                <TableCell sx={{ ...hCell, color: ACCENT }} align="right">Discount AIC</TableCell>
                <TableCell sx={{ ...hCell, color: ACCENT }} align="right">Discount $</TableCell>
                <TableCell sx={hCell} align="right">Net AIC</TableCell>
                <TableCell sx={hCell} align="right">Net $</TableCell>
                <TableCell sx={{ ...hCell, color: DISCOUNT_GREEN }} align="right">Overage Discount</TableCell>
                <TableCell sx={{ ...hCell, color: DELOITTE_PURPLE, pr: 0 }} align="right">Net Overage Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usageItems.map((item, idx) => {
                const isFullyCovered = item.netAmount === 0 && item.grossAmount > 0;
                return (
                  <TableRow
                    key={idx}
                    sx={{
                      "&:hover": { backgroundColor: "#f9fdf2" },
                      "&:last-child td": { border: 0 },
                    }}
                  >
                    <TableCell sx={{ ...bCell, pl: 0, fontWeight: 500 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        {isFullyCovered && (
                          <Tooltip title="Fully pool-covered - not billed" arrow placement="right">
                            <CheckCircleOutlineIcon
                              sx={{ fontSize: "0.9rem", color: ACCENT, flexShrink: 0 }}
                            />
                          </Tooltip>
                        )}
                        {item.model}
                      </Box>
                    </TableCell>
                    <TableCell sx={bCell} align="right">
                      {fmt(item.grossQuantity)}
                    </TableCell>
                    <TableCell sx={bCell} align="right">
                      {fmtUSD(item.grossAmount)}
                    </TableCell>
                    <TableCell sx={{ ...bCell, color: ACCENT, fontWeight: 600 }} align="right">
                      {item.discountQuantity > 0 ? `-${fmt(item.discountQuantity)}` : "-"}
                    </TableCell>
                    <TableCell sx={{ ...bCell, color: ACCENT, fontWeight: 600 }} align="right">
                      {item.discountAmount > 0 ? `-${fmtUSD(item.discountAmount)}` : "-"}
                    </TableCell>
                    <TableCell sx={{ ...bCell, fontWeight: 600 }} align="right">
                      {fmt(item.netQuantity)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bCell,
                        fontWeight: 700,
                        color: item.netAmount === 0 ? "#bbb" : "#1a1a1a",
                      }}
                      align="right"
                    >
                      {fmtUSD(item.netAmount)}
                    </TableCell>
                    <TableCell sx={{ ...bCell, fontWeight: 600, color: DISCOUNT_GREEN }} align="right">
                      {fmtUSD(
                        item.overageDiscountAmount ??
                          (item.netQuantity ?? 0) * (LIST_OVERAGE_RATE - effectiveDeloitteRate)
                      )}
                    </TableCell>
                    <TableCell sx={{ ...bCell, fontWeight: 600, color: DELOITTE_PURPLE, pr: 0 }} align="right">
                      {fmtUSD(
                        item.netOverageCostAmount ?? (item.netQuantity ?? 0) * effectiveDeloitteRate
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Totals row */}
              <TableRow sx={{ borderTop: "2px solid #e0e0e0" }}>
                <TableCell
                  sx={{ ...bCell, pl: 0, fontWeight: 700, color: "#1a1a1a", borderBottom: 0, pt: 1.5 }}
                >
                  Total
                </TableCell>
                <TableCell sx={{ ...bCell, fontWeight: 700, borderBottom: 0, pt: 1.5 }} align="right">
                  {fmt(totalGross)}
                </TableCell>
                <TableCell sx={{ ...bCell, fontWeight: 700, borderBottom: 0, pt: 1.5 }} align="right">
                  {fmtUSD(totalGrossAmt)}
                </TableCell>
                <TableCell
                  sx={{ ...bCell, fontWeight: 700, color: ACCENT, borderBottom: 0, pt: 1.5 }}
                  align="right"
                >
                  {totalDiscount > 0 ? `-${fmt(totalDiscount)}` : "-"}
                </TableCell>
                <TableCell
                  sx={{ ...bCell, fontWeight: 700, color: ACCENT, borderBottom: 0, pt: 1.5 }}
                  align="right"
                >
                  {totalDiscountAmt > 0 ? `-${fmtUSD(totalDiscountAmt)}` : "-"}
                </TableCell>
                <TableCell sx={{ ...bCell, fontWeight: 700, borderBottom: 0, pt: 1.5 }} align="right">
                  {fmt(totalNet)}
                </TableCell>
                <TableCell
                  sx={{ ...bCell, fontWeight: 700, borderBottom: 0, pt: 1.5 }}
                  align="right"
                >
                  {fmtUSD(totalNetAmt)}
                </TableCell>
                <TableCell
                  sx={{ ...bCell, fontWeight: 700, color: DISCOUNT_GREEN, borderBottom: 0, pt: 1.5 }}
                  align="right"
                >
                  {fmtUSD(totalOverageDiscountAmt)}
                </TableCell>
                <TableCell
                  sx={{ ...bCell, fontWeight: 700, color: DELOITTE_PURPLE, pr: 0, borderBottom: 0, pt: 1.5 }}
                  align="right"
                >
                  {fmtUSD(totalNetOverageCostAmt)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <Box sx={{ px: 3, py: 1.5, backgroundColor: "#fafafa" }}>
          <Typography sx={{ fontSize: "0.7rem", color: "#bbb" }}>
            $0.01 per AIC · Discount = pool-covered credits · Net = amount billed
          </Typography>
        </Box>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: "#115e00",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          px: 3,
          pt: 3,
          pb: 2.5,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.5)",
            "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Avatar + name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: "14px",
              backgroundColor: avatarColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "1.25rem",
              color: "#fff",
              flexShrink: 0,
              letterSpacing: "0.02em",
            }}
          >
            {initials}
          </Box>
          <Box>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
              {resolvedUser?.full_name ?? resolvedUser?.user_name ?? user.email}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.7)", mt: 0.25 }}>
              {user.email}
            </Typography>
            {costCenterName && (
              <Tooltip title={`Cost center: ${costCenterName}`} placement="bottom-start" arrow enterDelay={400}>
                <Typography noWrap sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.85)", mt: 0.25, maxWidth: 520 }}>
                  Cost center: <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>{costCenterName}</Box>
                </Typography>
              </Tooltip>
            )}
            {profileUser && (
              <Box sx={{ display: "flex", gap: 0.75, mt: 0.75, flexWrap: "wrap" }}>
                {[
                  { short: "Country", full: "Country", text: profileUser.country },
                  { short: "Dept", full: "Department", text: profileUser.department },
                  { short: "Function", full: "Business function", text: profileUser.business_function },
                ]
                  .filter((chip) => chip.text)
                  .map((chip) => (
                    <Tooltip key={chip.short} title={chip.full} placement="top" arrow>
                      <Box
                        sx={{ fontSize: "0.68rem", color: "#fff", backgroundColor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: "12px", px: 1.1, py: 0.15 }}
                      >
                        <Box component="span" sx={{ color: "rgba(255,255,255,0.65)", mr: 0.5 }}>
                          {chip.short}:
                        </Box>
                        {chip.text}
                      </Box>
                    </Tooltip>
                  ))}
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1.5, mt: 0.5, alignItems: "center", flexWrap: "wrap" }}>
              {/* The title falls back to user_name when there's no full name — don't repeat it. */}
              {(resolvedUser?.user_name ?? user.user_name) && resolvedUser?.full_name && (
                <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)" }}>
                  @{resolvedUser?.user_name ?? user.user_name}
                </Typography>
              )}
              {resolvedUser?.from_date && resolvedUser?.to_date && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.4)" }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>
                    {resolvedUser.from_date} → {resolvedUser.to_date}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {licensedElsewhere ? (
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px",
              px: 2,
              py: 1.4,
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#fff" }}>
              Usage can&apos;t be shown for this org
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.8)", mt: 0.5, lineHeight: 1.5 }}>
              This user also holds a Copilot license in {otherLicensedOrgNames.join(", ")}, so their usage can&apos;t be
              viewed here. Check those organizations for their full usage.
            </Typography>
          </Box>
        ) : noUsageFound ? (
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px",
              px: 2,
              py: 1.4,
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#fff" }}>
              No usage found for this month
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.8)", mt: 0.5 }}>
              This user has no Copilot usage recorded in this organization for the current month.
            </Typography>
          </Box>
        ) : (
        /* Mini stat row */
        <Box sx={{ display: "flex", gap: 1.5 }}>
          {/* The per-user usage response only carries these inside `totals`; the list
              rows carry total_consumed_credits / total_amount. A value that's missing
              everywhere shows "—" rather than a misleading 0. */}
          <MiniStat
            label="Total AIC"
            value={dashIfMissing(
              resolvedUser?.totals?.grossQuantity ?? resolvedUser?.total_consumed_credits ?? user.total_consumed_credits,
              fmt
            )}
          />
          <MiniStat
            label="Net Spend"
            value={dashIfMissing(
              resolvedUser?.total_amount ?? user.total_amount ?? resolvedUser?.totals?.netAmount,
              fmtUSD
            )}
            color="#fff"
          />
          <MiniStat
            label={hasDailyUsage ? "Days" : "Models Used"}
            value={hasDailyUsage ? (resolvedUser?.total_days ?? dailyUsage.length) : usageItems.length}
          />
          {profile && !profile.isLoading && !profile.error && <MiniStat label="Orgs Joined" value={joinedOrgs.length} />}
        </Box>
        )}
      </Box>

      <DialogContent sx={{ p: 0, backgroundColor: "#f8f8f8", display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        {footerNote && !licensedElsewhere && (
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#666",
              backgroundColor: "#fff",
              borderBottom: "1px solid #f0f0f0",
              px: 2,
              py: 0.75,
              flexShrink: 0,
            }}
          >
            {footerNote}
          </Typography>
        )}

        {/* Loading state while fetching API 2 detail */}
        {detailLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
            <CircularProgress sx={{ color: ACCENT }} size={36} />
          </Box>
        )}

        {/* ── Tabs (only when daily usage available) ── */}
        {!detailLoading && (hasDailyUsage || showRequestsTab || profile) && (
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fff",
              px: 2,
              minHeight: 44,
              flexShrink: 0,
            }}
            TabIndicatorProps={{ style: { backgroundColor: ACCENT } }}
          >
            {profile ? <Tab value="overview" label="Overview" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.85rem", minHeight: 44, color: "#555", "&.Mui-selected": { color: ACCENT } }} /> : <Tab value="summary" label="Summary" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.85rem", minHeight: 44, color: "#555", "&.Mui-selected": { color: ACCENT } }} />}
            {profile && <Tab value="orgs" label={`Organizations${joinedOrgs.length ? ` (${joinedOrgs.length})` : ""}`} sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.85rem", minHeight: 44, color: "#555", "&.Mui-selected": { color: ACCENT } }} />}
            {profile && canViewUsage && usageItems.length > 0 && <Tab value="usage" label="Usage" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.85rem", minHeight: 44, color: "#555", "&.Mui-selected": { color: ACCENT } }} />}
            {hasDailyUsage && canViewUsage && <Tab
              value="daily"
              label={`Daily (${dailyUsage.length} day${dailyUsage.length !== 1 ? "s" : ""})`}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.85rem", minHeight: 44, color: "#555", "&.Mui-selected": { color: ACCENT } }}
            />}
            {hasDailyUsage && canViewUsage && <Tab
              value="trend"
              label="Trend"
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.85rem", minHeight: 44, color: "#555", "&.Mui-selected": { color: ACCENT } }}
            />}
            {showRequestsTab && <Tab
              value="requests"
              label="Budget Requests"
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.85rem", minHeight: 44, color: "#555", "&.Mui-selected": { color: ACCENT } }}
            />}
          </Tabs>
        )}

        {/* ── Summary / fallback view ── */}
        {!detailLoading && (profile ? activeTab === "overview" : activeTab === "summary") && <Box sx={{ overflowY: "auto", flex: 1 }}>
        {/* Merged view: Profile and Budget sit side by side as two cards. */}
        <Box sx={profile ? { display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, p: 2.5, alignItems: "start", backgroundColor: "#f8f8f8" } : undefined}>
        {profile && (
          <Box>
            {profile.isLoading ? (
              <Typography sx={{ fontSize: "0.8rem", color: "#999" }}>Loading profile…</Typography>
            ) : profile.error ? (
              <Typography sx={{ fontSize: "0.8rem", color: "#d32f2f" }}>{profile.error}</Typography>
            ) : (
              <UserCostBillingCard compact user={profileUser} platformId={platformId} costCenterById={profile.costCenterById} />
            )}
          </Box>
        )}
        {/* ── Budget section ── */}
        <Box
          sx={
            profile
              ? { px: 2.25, py: 2, backgroundColor: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px" }
              : { px: 3, py: 2.5, backgroundColor: "#fff", borderBottom: "1px solid #f0f0f0" }
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#555",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mr: 0.5,
              }}
            >
              Budget
            </Typography>
            <Tooltip title="Copilot budget" placement="top" arrow>
              <CopilotIcon aria-label="Copilot" sx={{ fontSize: 16, color: "#1a1a1a", ml: -0.5 }} />
            </Tooltip>
            {!hasBar && !requestBudget && (
              <Chip
                label="NOT SET"
                size="small"
                sx={{ fontSize: "0.68rem", fontWeight: 700, height: 20, backgroundColor: "#f0f0f0", color: "#999" }}
              />
            )}
            {/* Beside the heading, with the times in bold so they stand out. */}
            {budget?.updated_at && (
              <>
                <BudgetMetaDot />
                <Typography sx={{ fontSize: "0.76rem", color: "#777" }}>
                  Updated{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
                    {formatLocalDateTime(budget.updated_at)}
                  </Box>
                </Typography>
              </>
            )}
            {budget && budget.last_activity_at !== undefined && (
              <>
                <BudgetMetaDot />
                <Typography sx={{ fontSize: "0.76rem", color: "#777" }}>
                  Last Copilot activity{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
                    {budget.last_activity_at ? formatLocalDateTime(budget.last_activity_at) : "No activity recorded"}
                  </Box>
                </Typography>
              </>
            )}
          </Box>

          {hasBar ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.7 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>
                  {fmtUSD(budget.consumed_amount)} consumed of {fmtUSD(budget.budget_amount)}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: getBudgetTextColor(barColor) }}>
                  {budgetPct.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={budgetPct}
                sx={{
                  height: 8,
                  borderRadius: 8,
                  backgroundColor: "#f0f0f0",
                  "& .MuiLinearProgress-bar": { backgroundColor: barColor, borderRadius: 8 },
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: isOverBudget ? USAGE_RED : "#aaa",
                  fontWeight: isOverBudget ? 600 : 400,
                  mt: 0.8,
                }}
              >
                {isOverBudget
                  ? `Over budget by ${fmtUSD(budget.consumed_amount - budget.budget_amount)}`
                  : `${fmtUSD(budget.budget_amount - budget.consumed_amount)} remaining`}
              </Typography>
              {licensedOrgNames.length > 0 && (
                <Typography sx={{ fontSize: "0.72rem", color: "#1a1a1a", fontWeight: 700, mt: 0.8 }}>
                  Copilot licensed from: {licensedOrgNames.join(", ")}
                </Typography>
              )}
            </Box>
          ) : requestBudget ? (
            <Typography sx={{ fontSize: "0.85rem", color: "#1a1a1a" }}>
              <strong>{fmtUSD(requestBudget.budget_amount)}</strong>
              {requestBudget.budget_scope ? ` (${requestBudget.budget_scope} budget)` : ""}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: "0.8rem", color: "#bbb", fontStyle: "italic" }}>
              No budget has been configured for this user.
            </Typography>
          )}
        </Box>

        </Box>
        {profile && !profile.isLoading && !profile.error && joinedOrgs.length > 0 && (
          <Box sx={{ mx: 2.5, mb: 2.5, px: 2.25, py: 1.5, backgroundColor: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#0b756c", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Where they've joined
              </Typography>
              <Box component="button" type="button" onClick={() => setActiveTab("orgs")} sx={{ border: 0, background: "none", color: ACCENT, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", p: 0 }}>
                See all
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {joinedOrgs.slice(0, 6).map((org) => (
                <Box key={org.id} sx={{ fontSize: "0.75rem", color: "#1e293b", backgroundColor: "#f6f8fa", border: "1px solid #e8edf2", borderRadius: "12px", px: 1.1, py: 0.2 }}>
                  {profile.orgIdToName?.[org.id] || org.id}
                  {licensedOrgIds.has(org.id) && " · Copilot"}
                </Box>
              ))}
              {joinedOrgs.length > 6 && <Typography sx={{ fontSize: "0.75rem", color: "#888", alignSelf: "center" }}>+{joinedOrgs.length - 6} more</Typography>}
            </Box>
          </Box>
        )}
        {!profile && usageBreakdown}
        </Box>}

        {/* ── Daily breakdown view ── */}
        {!detailLoading && hasDailyUsage && activeTab === "daily" && (
          <Box sx={{ overflowY: "auto", flex: 1, p: 2 }}>
            {dailyUsage.map((day) => {
              const isExpanded = !!expandedDays[day.date];
              // API returns usage_stats pre-sorted desc by grossQuantity.
              const dayItems = day.usage_stats ?? day.usageItems ?? [];
              // Per-day totals aren't returned on the day object itself — sum from usage_stats.
              const dayGrossQty = dayItems.reduce((s, i) => s + (i.grossQuantity ?? 0), 0);
              const dayNetAmt = dayItems.reduce((s, i) => s + (i.netAmount ?? 0), 0);
              return (
                <Box
                  key={day.date}
                  sx={{ mb: 1.5, border: "1px solid #e8e8e8", borderRadius: "10px", overflow: "hidden", backgroundColor: "#fff" }}
                >
                  {/* Day header row */}
                  <Box
                    onClick={() => toggleDay(day.date)}
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      px: 2.5, py: 1.5, cursor: "pointer", userSelect: "none",
                      backgroundColor: isExpanded ? "#f9fdf2" : "#fff",
                      "&:hover": { backgroundColor: "#f9fdf2" },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#1a1a1a" }}>
                      {fmtDate(day.date)}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: "0.62rem", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em" }}>Gross AIC</Typography>
                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#1a1a1a" }}>{fmt(dayGrossQty)}</Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: "0.62rem", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em" }}>Net Spend</Typography>
                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: ACCENT }}>{fmtUSD(dayNetAmt)}</Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); toggleDay(day.date); }}
                        sx={{ color: "#888", p: 0.5 }}
                      >
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Collapsible model table */}
                  <Collapse in={isExpanded}>
                    <Divider />
                    <TableContainer sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ ...hCell, pl: 0 }}>Model</TableCell>
                            <TableCell sx={hCell} align="right">Gross AIC</TableCell>
                            <TableCell sx={{ ...hCell, color: ACCENT }} align="right">Discount AIC</TableCell>
                            <TableCell sx={hCell} align="right">Net AIC</TableCell>
                            <TableCell sx={hCell} align="right">Net $</TableCell>
                            <TableCell sx={{ ...hCell, color: DISCOUNT_GREEN }} align="right">Overage Discount</TableCell>
                            <TableCell sx={{ ...hCell, color: DELOITTE_PURPLE, pr: 0 }} align="right">Net Overage Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dayItems.map((item, idx) => (
                            <TableRow key={idx} sx={{ "&:last-child td": { border: 0 } }}>
                              <TableCell sx={{ ...bCell, pl: 0, fontWeight: 500 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                  {item.netAmount === 0 && item.grossAmount > 0 && (
                                    <Tooltip title="Fully pool-covered - not billed" arrow placement="right">
                                      <CheckCircleOutlineIcon sx={{ fontSize: "0.9rem", color: ACCENT, flexShrink: 0 }} />
                                    </Tooltip>
                                  )}
                                  {item.model}
                                </Box>
                              </TableCell>
                              <TableCell sx={bCell} align="right">{fmt(item.grossQuantity)}</TableCell>
                              <TableCell sx={{ ...bCell, color: ACCENT, fontWeight: 600 }} align="right">
                                {item.discountQuantity > 0 ? `-${fmt(item.discountQuantity)}` : "-"}
                              </TableCell>
                              <TableCell sx={bCell} align="right">{fmt(item.netQuantity)}</TableCell>
                              <TableCell sx={{ ...bCell, fontWeight: 700, color: item.netAmount === 0 ? "#bbb" : "#1a1a1a" }} align="right">
                                {fmtUSD(item.netAmount)}
                              </TableCell>
                              <TableCell sx={{ ...bCell, fontWeight: 600, color: DISCOUNT_GREEN }} align="right">
                                {fmtUSD(
                                  item.overageDiscountAmount ??
                                    (item.netQuantity ?? 0) * (LIST_OVERAGE_RATE - effectiveDeloitteRate)
                                )}
                              </TableCell>
                              <TableCell sx={{ ...bCell, pr: 0, fontWeight: 600, color: DELOITTE_PURPLE }} align="right">
                                {fmtUSD(
                                  item.netOverageCostAmount ?? (item.netQuantity ?? 0) * effectiveDeloitteRate
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── Merged view: where the user has joined, with Copilot licenses ── */}
        {profile && activeTab === "orgs" && (
          <Box sx={{ overflowY: "auto", flex: 1, p: 3, backgroundColor: "#fff" }}>
            {profile.isLoading ? (
              <Typography sx={{ fontSize: "0.85rem", color: "#999" }}>Loading organizations…</Typography>
            ) : profile.error ? (
              <Typography sx={{ fontSize: "0.85rem", color: "#d32f2f" }}>{profile.error}</Typography>
            ) : joinedOrgs.length === 0 ? (
              <Typography sx={{ fontSize: "0.85rem", color: "#999" }}>No joined organizations found for this user.</Typography>
            ) : (
              <UserOrganizationsTable orgs={joinedOrgs} orgIdToName={profile.orgIdToName} licensedOrgIds={licensedOrgIds} maxHeight={360} />
            )}
          </Box>
        )}

        {/* ── Merged view: model usage on its own tab ── */}
        {profile && canViewUsage && !detailLoading && activeTab === "usage" && (
          <Box sx={{ overflowY: "auto", flex: 1 }}>{usageBreakdown}</Box>
        )}

        {/* ── Every budget request this user has made in this org ── */}
        {showRequestsTab && activeTab === "requests" && (
          <UserRequestsTab email={resolvedUser?.email ?? user?.email} orgId={orgId} platformId={platformId} open={open} />
        )}

        {/* ── Trend view — all models, day by day ── */}
        {!detailLoading && hasDailyUsage && activeTab === "trend" && (
          <Box sx={{ overflowY: "auto", flex: 1, p: 3, backgroundColor: "#fff" }}>
            {trendRows.length < 2 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "#999" }}>
                  A trend needs at least 2 days of usage — this range only has{" "}
                  {trendRows.length} day of activity.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Daily Usage by Model
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#999", mb: 2.5, mt: 0.5 }}>
                  Gross AIC consumed per model, per day — hover to see spend in USD
                </Typography>
                <Box sx={{ "& svg, & svg *": { outline: "none" } }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendRows} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: "#999" }}
                        axisLine={false}
                        tickLine={false}
                        interval={getTickInterval(trendRows.length)}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#999" }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                        width={45}
                      />
                      <RechartsTooltip content={<ModelTrendTooltip />} cursor={{ stroke: "#cfcfcf", strokeWidth: 1, strokeDasharray: "4 4" }} />
                      <Legend
                        wrapperStyle={{ fontSize: "0.75rem" }}
                        formatter={(value) => <span style={{ color: "#555" }}>{value}</span>}
                      />
                      {trendModelKeys.map((model, index) => {
                        const color = MODEL_LINE_COLORS[index % MODEL_LINE_COLORS.length];
                        return (
                          <Line
                            key={model}
                            type="monotone"
                            dataKey={model}
                            stroke={color}
                            strokeWidth={2}
                            dot={trendRows.length <= 15 ? { r: 2.5, fill: color, strokeWidth: 0 } : false}
                            activeDot={{ r: 4, strokeWidth: 0 }}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailModal;
